// REALISTIC HORSE (2026-11-04, owner: realistic models; Sketchfab token given,
// "modify a bit the 3D model … my game should be unique", commercial use).
// A rigged, animated horse — "Horse" by henry_7, CC-BY 4.0 — reworked by
// agent/horse-asset/pipeline.mjs (new coat, darker mane, teal blanket, four
// clips). See public/models/horse/CREDITS.md and assetCredits.ts.
//
// VehicleMesh owns the lifecycle: a horse whose module is ARMED hides its
// procedural body (the nameplate stays) and drives one HorseModel instead.
//
// Contract (same as zombieModel.ts):
// - Armed ONLY by warmHorseModel() under the match load screen, after one real
//   frame through the game's render path compiled its programs (colour +
//   skinned shadow depth) and uploaded its textures. Not loaded by then → the
//   round keeps the procedural horse; it never pops in mid-fight.
// - Geometry/textures are shared (cached GLB); the COAT material is cloned per
//   horse so each one carries its own tint (rider colour) — a uniform, so every
//   tint hits the same compiled program.
// - No per-frame allocation: update() writes weights/timeScales on
//   preallocated actions and calls mixer.update once.
import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { gltfWithin, instantiate, loadGltf, warmStageNow, type WarmStage } from "./gltfAssets.js";

export const HORSE_GLB_URL = "/models/horse/horse.glb";

/** Fit target: the procedural horse's ear tips stand at ~2.75 m and its saddle
 *  seat at ~2.0 m (worldEntities.ts), which is where mounted riders are drawn.
 *  The model is scaled so its bind-pose height matches. */
const TARGET_HEIGHT = 2.75;

const CLIPS = ["idle", "walk", "gallop"] as const;
const IDLE = 0, WALK = 1, GALLOP = 2;
/** Ground speeds the in-place loops read right at (m/s, after the fit). */
const WALK_NOMINAL = 2.4;
const GALLOP_NOMINAL = 7.0;
/** Hysteretic gait bands (m/s). Ridden horses top out around 6 m/s. */
const WALK_ON = 0.5, WALK_OFF = 0.25, GALLOP_ON = 3.4, GALLOP_OFF = 2.8;
const FAR_STEP = 1 / 15;

let template: GLTF | null = null;
/** Fit computed once from the template: yaw so the head faces −Z, uniform
 *  scale, and the offset that puts the hooves at y 0 and the body centred. */
let fitYaw = 0, fitScale = 1;
const fitOffset = new THREE.Vector3();
let coatName = "";
let armed = false;
let armedFor: object | null = null;

export function horseModelArmed(): boolean { return armed && template !== null; }
export function disarmHorseModel(): void { armed = false; armedFor = null; }

function prepare(g: GLTF | null): GLTF | null {
  if (!g || template) return template ?? g;
  g.scene.updateMatrixWorld(true);
  // precise bounds at bind pose (Box3 skins the vertices) — once, ~20k verts
  const box = new THREE.Box3().setFromObject(g.scene, true);
  const size = box.getSize(new THREE.Vector3());
  if (!(size.y > 0) || !Number.isFinite(size.y)) { console.error("[horse] unexpected GLB bounds"); return null; }
  // the LONG horizontal axis is nose→tail; the head end is the one whose top
  // reaches higher (ears) — sample the front/back 20 % of that axis
  const alongX = size.x > size.z;
  const lo = alongX ? box.min.x : box.min.z, hi = alongX ? box.max.x : box.max.z;
  let topLo = -Infinity, topHi = -Infinity;
  const v = new THREE.Vector3();
  g.scene.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh) return;
    const pos = m.geometry.getAttribute("position");
    for (let i = 0; i < pos.count; i += 3) {
      m.getVertexPosition(i, v);
      v.applyMatrix4(m.matrixWorld);
      const a = alongX ? v.x : v.z;
      if (a < lo + (hi - lo) * 0.2) topLo = Math.max(topLo, v.y);
      else if (a > hi - (hi - lo) * 0.2) topHi = Math.max(topHi, v.y);
    }
  });
  const headAtHi = topHi > topLo;
  // rotate so the head end points to −Z (vehicle forward)
  if (alongX) fitYaw = headAtHi ? Math.PI / 2 : -Math.PI / 2; // +X → −Z needs +90°
  else fitYaw = headAtHi ? Math.PI : 0;                        // +Z → −Z needs 180°
  fitScale = TARGET_HEIGHT / size.y;
  const c = box.getCenter(new THREE.Vector3());
  fitOffset.set(-c.x, -box.min.y, -c.z);
  g.scene.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh) return;
    // bounding sphere once at bind pose, padded for the gallop's reach
    m.computeBoundingSphere();
    if (m.boundingSphere) m.boundingSphere.radius *= 1.3;
    m.castShadow = true;
    m.receiveShadow = true;
    const mat = m.material as THREE.MeshStandardMaterial;
    if (mat.name === "Horse") coatName = mat.name;
  });
  if (!coatName) { console.error("[horse] coat material not found"); return null; }
  template = g;
  console.log(`[horse] model ready: ${size.x.toFixed(2)}×${size.y.toFixed(2)}×${size.z.toFixed(2)} → scale ${fitScale.toFixed(3)}, yaw ${fitYaw.toFixed(2)}`);
  return g;
}

/** Start the fetch early (match start). Safe to call repeatedly. */
export function preloadHorseModel(): void {
  void loadGltf(HORSE_GLB_URL).then(prepare);
}

/** The GLB is parsed and fitted (no arming implied). For views that compile
 *  their OWN context — the lobby map preview (previews.ts) runs compileAsync
 *  over its scene, so it may mount the model as soon as it is loaded. */
export function horseModelLoaded(): boolean { return template !== null; }
/** Load + fit (cached). Resolves whether the model is usable. */
export function loadHorseModel(): Promise<boolean> {
  return loadGltf(HORSE_GLB_URL).then(prepare).then(() => template !== null);
}

export class HorseModel {
  /** Container in vehicle-group space. */
  readonly root = new THREE.Group();
  private readonly fit = new THREE.Group();
  private readonly inner: THREE.Object3D;
  private readonly mixer: THREE.AnimationMixer;
  private readonly acts: THREE.AnimationAction[] = [];
  private readonly w = new Float32Array(CLIPS.length);
  private readonly skinned: THREE.SkinnedMesh[] = [];
  /** Per-horse coat (cloned so the tint is per instance). */
  readonly coat: THREE.MeshStandardMaterial;
  private gait = IDLE;
  private farAcc = 0;
  private readonly jitter = 0.94 + Math.random() * 0.12;

  constructor() {
    const g = template!;
    this.inner = instantiate(g);
    this.fit.rotation.y = fitYaw;
    this.fit.scale.setScalar(fitScale);
    this.inner.position.copy(fitOffset);
    this.fit.add(this.inner);
    this.root.add(this.fit);
    this.root.name = "npHorseModel";
    let coat: THREE.MeshStandardMaterial | null = null;
    this.inner.traverse((o) => {
      const m = o as THREE.SkinnedMesh;
      if (!m.isSkinnedMesh) return;
      const mat = m.material as THREE.MeshStandardMaterial;
      if (mat.name === coatName) {
        if (!coat) { coat = mat.clone(); coat.userData.shared = false; }
        m.material = coat;
      }
      this.skinned.push(m);
    });
    this.coat = coat!;
    this.mixer = new THREE.AnimationMixer(this.inner);
    for (const name of CLIPS) {
      const clip = THREE.AnimationClip.findByName(g.animations, name);
      const a = this.mixer.clipAction(clip);
      a.play();
      a.setEffectiveWeight(name === "idle" ? 1 : 0);
      a.time = Math.random() * clip.duration; // a herd never moves in lockstep
      this.acts.push(a);
    }
    this.w[IDLE] = 1;
    this.mixer.update(0);
  }

  /** Once per rendered frame. `speed` = horizontal m/s. */
  update(dt: number, speed: number, far: boolean): void {
    if (far) {
      this.farAcc += dt;
      if (this.farAcc < FAR_STEP) return;
      dt = this.farAcc;
    }
    this.farAcc = 0;
    const gallop = this.gait === GALLOP ? speed > GALLOP_OFF : speed > GALLOP_ON;
    const walk = this.gait !== IDLE ? speed > WALK_OFF : speed > WALK_ON;
    this.gait = gallop ? GALLOP : walk ? WALK : IDLE;
    const k = 1 - Math.exp(-dt * 7);
    for (let i = 0; i < this.acts.length; i++) {
      this.w[i] += ((i === this.gait ? 1 : 0) - this.w[i]) * k;
      this.acts[i].setEffectiveWeight(this.w[i]);
    }
    this.acts[WALK].timeScale = Math.min(1.8, Math.max(0.6, speed / WALK_NOMINAL)) * this.jitter;
    this.acts[GALLOP].timeScale = Math.min(1.45, Math.max(0.75, speed / GALLOP_NOMINAL)) * this.jitter;
    this.acts[IDLE].timeScale = this.jitter;
    this.mixer.update(dt);
  }

  skinnedMeshes(): readonly THREE.SkinnedMesh[] { return this.skinned; }

  /** Free the per-instance pieces: bone textures, mixer bindings, the coat. */
  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.inner);
    for (const m of this.skinned) m.skeleton.dispose();
    this.coat.dispose();
    this.root.removeFromParent();
  }
}

interface WarmTarget { readonly scene: THREE.Scene; readonly camera: THREE.Camera; render(): void }

/** Under the match load screen: wait (bounded) for the GLB, render ONE real
 *  frame with a horse in front of the camera (compiles the skinned colour +
 *  shadow programs, uploads textures), then arm. Caller gates weak devices. */
export async function warmHorseModel(r: WarmTarget, timeoutMs = 3000): Promise<boolean> {
  return warmStageNow(r, await stageHorseWarm(r, timeoutMs));
}

/** Batched form (gltfAssets WarmStage): wait for the GLB, pose one horse in
 *  front of the camera; precompile() compiles + draws it with everything else,
 *  then `arm()` + `dispose()`. Null = not ready by the deadline (procedural round). */
export async function stageHorseWarm(r: WarmTarget, timeoutMs = 3000): Promise<WarmStage | null> {
  if (armed && armedFor === r) return { root: null, arm() { /* already warm */ }, dispose() { /* nothing staged */ } };
  armed = false;
  armedFor = null;
  const g = await gltfWithin(loadGltf(HORSE_GLB_URL).then(prepare), timeoutMs);
  if (!g || !template) { console.log("[horse] model not ready at the load-screen deadline — procedural horses this round"); return null; }
  const h = new HorseModel();
  const cam = r.camera;
  const fwd = new THREE.Vector3();
  cam.getWorldDirection(fwd);
  h.root.position.copy(cam.position).addScaledVector(fwd, 5);
  h.root.position.y -= 1.5;
  for (const m of h.skinnedMeshes()) m.frustumCulled = false;
  h.update(1 / 60, 0, false);
  return { root: h.root, arm: () => { armed = true; armedFor = r; }, dispose: () => h.dispose() };
}
